'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

const DEFAULT_LOADING_DISPLAY_MS = 5000; // Show "authenticating" for 5 seconds by default

type UseSocialAuthLoadingReturn = {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
};

type UseSocialAuthLoadingOptions = {
  displayMs?: number;
};

/**
 * Hook to manage temporary "authenticating" loading state for social auth buttons.
 * Shows loading state for a few seconds to indicate the flow has started,
 * then re-enables the button so users can retry if needed.
 * @param options.displayMs - Duration in ms to show loading state (default: 5000)
 */
export const useSocialAuthLoading = (
  options: UseSocialAuthLoadingOptions = {},
): UseSocialAuthLoadingReturn => {
  const { displayMs = DEFAULT_LOADING_DISPLAY_MS } = options;
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const startLoading = useCallback(() => {
    cleanup();
    setIsLoading(true);
    timerRef.current = setTimeout(() => {
      setIsLoading(false);
      timerRef.current = null;
    }, displayMs);
  }, [cleanup, displayMs]);

  const stopLoading = useCallback(() => {
    cleanup();
    setIsLoading(false);
  }, [cleanup]);

  return {
    isLoading,
    startLoading,
    stopLoading,
  };
};
